import type * as React from 'react';
import { useMemo, useState } from 'react';
import { FileSpreadsheetIcon, PlusIcon, TableIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { ExportMenu, FORMAT_XLSX } from '../../statistics/components/ExportMenu';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../../../components/ui/empty';
import { Field, FieldDescription, FieldLabel } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Spinner } from '../../../components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Textarea } from '../../../components/ui/textarea';
import { extractShortcode } from '../../statistics/lib/parse-instagram-url';
import { extractLinks } from '../lib/extract-links';
import { patchFromScreen, type PostRow } from '../lib/post-rows';
import type { ScreenImage } from '../lib/render/insights-canvas';
import {
  buildSeedingXlsx,
  sheetTitle,
  type SeedingRow,
  type XlsxImage,
} from '../lib/xlsx/seeding-book';
import { usePostRows } from '../model/usePostRows';
import { CellInput } from './cell-input';
import { ImagePasteProvider } from './image-field';
import { ScreenCell } from './screen-cell';

/**
 * «Посев» — перечень размещений в Excel по образцу клиента.
 *
 * Тот же список ссылок, что у презентации и акта, но на выходе таблица, с
 * которой работает менеджер клиента: строка на публикацию, экран
 * статистики картинкой прямо в ячейке, никаких итогов — суммы считает тот,
 * кто открыл файл.
 *
 * Строки живут в общей модели (use-post-rows): собираются по ссылкам,
 * правятся руками, к строкам без цифр скриншот приносят сами.
 */

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

async function toXlsxImage(screen: ScreenImage | null): Promise<XlsxImage | null> {
  if (!screen) return null;
  return {
    data: new Uint8Array(await screen.blob.arrayBuffer()),
    width: screen.width,
    height: screen.height,
    type: screen.blob.type === 'image/png' ? 'png' : 'jpg',
  };
}

function download(bytes: Uint8Array<ArrayBuffer>, filename: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([bytes], { type: XLSX_TYPE }));
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export function SeedingReport(): React.ReactElement {
  const table = usePostRows();
  const [linksText, setLinksText] = useState('');
  const [sheet, setSheet] = useState('Посев');
  const [isSaving, setIsSaving] = useState(false);

  const links = useMemo(() => extractLinks(linksText), [linksText]);
  const valid = useMemo(() => links.filter((url) => extractShortcode(url)).length, [links]);

  const { rows, isBusy, progress } = table;
  const withoutScreen = rows.filter((row) => !row.screen).length;

  async function handleDownload(): Promise<void> {
    setIsSaving(true);
    try {
      const screens = await Promise.all(rows.map((row) => toXlsxImage(row.screen)));
      const sheetRows: SeedingRow[] = rows.map((row, i) => ({
        username: row.resource,
        format: row.format,
        url: row.url,
        views: row.views,
        reach: row.reach,
        screen: screens[i],
      }));
      const name = sheetTitle(sheet);
      download(
        buildSeedingXlsx({ sheet: name, rows: sheetRows }),
        /Посев/i.test(name) ? `${name}.xlsx` : `Посев ${name}.xlsx`,
      );
      toast.success('Таблица собрана');
    } catch (err) {
      console.error('[Посев] книга не собралась:', err);
      toast.error(err instanceof Error ? err.message : 'Не удалось собрать таблицу');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <ImagePasteProvider>
      <div className="space-y-6">
        <Field>
          <FieldLabel htmlFor="seeding-links">Ссылки на публикации</FieldLabel>
          <Textarea
            id="seeding-links"
            rows={5}
            placeholder={'https://www.instagram.com/p/…\nhttps://www.instagram.com/reel/…'}
            value={linksText}
            disabled={isBusy}
            onChange={(e) => setLinksText(e.target.value)}
          />
          <div className="flex items-center gap-3">
            <FieldDescription className="flex-1">
              {links.length === 0
                ? 'По одной в строке, в порядке строк таблицы'
                : `Ссылок: ${valid}${links.length > valid ? `, пропустим: ${links.length - valid}` : ''}`}
            </FieldDescription>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={isBusy || links.length === 0}
              onClick={() => void table.collect(linksText)}
            >
              {isBusy ? <Spinner /> : <TableIcon />}
              Собрать таблицу
            </Button>
          </div>
        </Field>

        {progress ? <p className="text-muted-foreground text-sm">{progress}</p> : null}

        {rows.length === 0 ? (
          <Empty className="border-dashed">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileSpreadsheetIcon />
              </EmptyMedia>
              <EmptyTitle>Таблицы пока нет</EmptyTitle>
              <EmptyDescription>
                Паблик, формат, просмотры и охваты подставим сами, экран статистики нарисуем.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="space-y-3">
            <Table className="min-w-3xl">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">№</TableHead>
                  <TableHead className="w-44">Паблик</TableHead>
                  <TableHead className="w-28">Формат</TableHead>
                  <TableHead className="min-w-64">Ссылка</TableHead>
                  <TableHead className="w-24 text-right">Просмотры</TableHead>
                  <TableHead className="w-24 text-right">Охваты</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <SeedingTableRow
                    key={row.key}
                    row={row}
                    index={i}
                    disabled={isBusy}
                    onPatch={(patch) => table.patchRow(row.key, patch)}
                    onRemove={() => table.removeRow(row.key)}
                  />
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center gap-3">
              <FieldDescription className="flex-1">
                Строк: {rows.length}
                {withoutScreen > 0 ? `, без скриншота: ${withoutScreen}` : ''}
              </FieldDescription>
              <Button variant="ghost" size="sm" className="gap-1" disabled={isBusy} onClick={table.addRow}>
                <PlusIcon />
                Добавить строку
              </Button>
            </div>

            <div className="flex flex-col gap-2 pt-1 sm:flex-row">
              <Input
                id="seeding-sheet"
                className="sm:w-56"
                aria-label="Название листа"
                placeholder="Название листа"
                value={sheet}
                disabled={isBusy}
                onChange={(e) => setSheet(e.target.value)}
              />
              <ExportMenu
                rememberKey="report:seeding-sheet"
                label="Выгрузить"
                formats={[FORMAT_XLSX]}
                onSelect={handleDownload}
                disabled={isBusy}
                busy={isSaving}
                busyLabel="Собираем таблицу…"
                className="flex-1"
              />
            </div>
          </div>
        )}
      </div>
    </ImagePasteProvider>
  );
}

function SeedingTableRow({
  row,
  index,
  disabled,
  onPatch,
  onRemove,
}: {
  row: PostRow;
  index: number;
  disabled: boolean;
  onPatch: (patch: Partial<PostRow>) => void;
  onRemove: () => void;
}): React.ReactElement {
  const at = index + 1;
  return (
    <TableRow>
      <TableCell className="text-muted-foreground tabular-nums">{at}</TableCell>
      <TableCell>
        <CellInput
          label={`Паблик, строка ${at}`}
          placeholder="@kazpress"
          value={row.resource}
          disabled={disabled}
          onChange={(resource) => onPatch({ resource })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Формат, строка ${at}`}
          placeholder="Пост"
          value={row.format}
          disabled={disabled}
          onChange={(format) => onPatch({ format })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Ссылка, строка ${at}`}
          value={row.url}
          disabled={disabled}
          onChange={(url) => onPatch({ url })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Просмотры, строка ${at}`}
          value={row.views}
          align="right"
          disabled={disabled}
          onChange={(views) => onPatch({ views })}
        />
      </TableCell>
      <TableCell>
        <CellInput
          label={`Охваты, строка ${at}`}
          value={row.reach}
          align="right"
          disabled={disabled}
          onChange={(reach) => onPatch({ reach })}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-0.5">
          <ScreenCell
            screen={row.screen}
            title={row.resource || row.url}
            fieldId={row.key}
            disabled={disabled}
            onChange={(screen) => onPatch({ screen })}
            onRecognized={(data) => onPatch(patchFromScreen(row, data))}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Убрать строку ${at}`}
            disabled={disabled}
            onClick={onRemove}
          >
            <Trash2Icon className="text-muted-foreground" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
